"use client"

import { useSyncExternalStore } from "react"
import { AdCard } from "@/components/ad-card"
import { AD_CONFIGS } from "@/lib/ad-config"

// Picked once on the client, then stable. Module scope keeps Math.random() out
// of render and gives useSyncExternalStore the identical reference it needs.
const subscribeNever = () => () => {}
let cachedTopAd: (typeof ADDITIONAL_ADS)[number] | null = null
let cachedStuckAd: (typeof HIGH_PAYOUT_ADS)[number] | null = null
const getTopAd = () => {
  if (!cachedTopAd) cachedTopAd = ADDITIONAL_ADS[Math.floor(Math.random() * ADDITIONAL_ADS.length)]
  return cachedTopAd
}
const getStuckAd = () => {
  if (!cachedStuckAd) cachedStuckAd = HIGH_PAYOUT_ADS[Math.floor(Math.random() * HIGH_PAYOUT_ADS.length)]
  return cachedStuckAd
}

const HIGH_PAYOUT_ADS = [
  AD_CONFIGS.wayfair["300x250"],
  AD_CONFIGS.lg["300x250"],
]
const ADDITIONAL_ADS = [
  AD_CONFIGS.ajmadison["300x250"],
  AD_CONFIGS.fridgefilters["300x250"],
  AD_CONFIGS.sharpSquare["300x250"],
]

interface TagSidebarWithAdsProps {
  children: React.ReactNode
}

/**
 * Sidebar layout for blog tag pages: ad above Related Tags, ad below; on
 * desktop the ad below sticks (FRIDGE-1567). Same ad pools as /blogs/news,
 * random on the client.
 */
export function TagSidebarWithAds({ children }: TagSidebarWithAdsProps) {
  // Random pick lives in a module-level cache read through
  // useSyncExternalStore, not pushed into state from an effect. Math.random()
  // must not run during render (react-hooks/purity), and caching keeps the
  // snapshot reference stable so the ads do not re-roll on unrelated
  // re-renders. The first (deterministic) entry is still what the server and
  // the hydration pass render, exactly as the useState defaults did.

  const topAd = useSyncExternalStore(subscribeNever, getTopAd, () => ADDITIONAL_ADS[0])
  const stuckAd = useSyncExternalStore(subscribeNever, getStuckAd, () => HIGH_PAYOUT_ADS[0])

  // FRIDGE-1567: only the sidebar's last block sticks. The whole sidebar (an
  // ad, Related Tags, an ad) is 1,050 to 1,240 px tall, and the room between
  // the header and the chat pill's strip is 708 px in a 900 px window, 566 px
  // while the price-watch card shows. Kept stuck as one capped box that
  // scrolled inside itself (ce4ab7d0), its lower links sat below the box's cut
  // edge, inside the window, right above the card (production 10/17/2026, card
  // open: "Counter-Depth Refrigerators", "Built-In Refrigerators", "View all
  // tags" and the second ad at 1024), where a click at their centre landed on
  // the card and the cut read as the sidebar running under it.
  //
  // Now the first ad and Related Tags scroll with the page, and the last block,
  // one ad of 278 to 400 px, sticks under the header (`lg:top-24`, 96 px) in
  // the same capped box (`tag-sidebar-sticky` in app/globals.css), so the
  // stuck part fits whole above the pill or the card and nothing in it is cut.
  // The column fills its grid row (`lg:h-full`) so the last block has room to
  // stick; below 1024 px the sidebar sits under the articles and nothing
  // sticks. The high-payout ad takes the stuck slot, so it is still the ad
  // that stays in view the whole time the sidebar is stuck.
  //
  // Before: FRIDGE-1507 P3 (10/14/2026) gave the whole sidebar a negative
  // sticky top so its bottom stopped above the pill, which put its top above
  // the window and under the header instead (production 10/16/2026).
  return (
    <div className="space-y-5 w-full lg:h-full">
      {topAd && <AdCard {...topAd} variant="blog" className="w-full" />}
      {children}
      {stuckAd && (
        <div className="tag-sidebar-sticky lg:sticky lg:top-24">
          <AdCard {...stuckAd} variant="blog" className="w-full" />
        </div>
      )}
    </div>
  )
}
